import { useEffect, useRef } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar, toast } from '@mint-lib/notifications';

import { Button } from '@myn/mui';

type UseSettingsNotificationType = {
  prevLoadingState: boolean | undefined;
  loadingState: boolean;
  errorState: unknown;
  retryAction: () => void;
};

type UseSettingsNotificationReturnType = {};

const useSettingsNotifications = ({
  prevLoadingState,
  loadingState,
  errorState,
  retryAction,
}: UseSettingsNotificationType): UseSettingsNotificationReturnType => {
  const { t } = useTranslation('@myn/planning');

  const isInitial = useRef(true);
  const lastSnackbarKey = useRef<string | number>(null);

  const closeLastSnackbar = () => {
    lastSnackbarKey.current && toast.dismiss(lastSnackbarKey.current);
  };

  const handleRetry = () => {
    closeLastSnackbar();
    retryAction();
  };

  const showSuccessNotification = () => {
    return enqueueSnackbar({
      variant: 'success',
      title: t('Success'),
      subTitle: t(
        'Changes applied successfully! Please note that the changes might not be able to influence the next suggestion you receive, as that suggestion may already be in the progress state. The next suggestion affected by the changed settings will be received not earlier than tomorrow.',
      ),
      autoHideDuration: 5000,
      removable: true,
    });
  };

  const showErrorNotification = () => {
    return enqueueSnackbar({
      variant: 'error',
      title: t('Error'),
      subTitle: t('Something went wrong when saving changes'),
      removable: true,
      actions: (
        <Button size="small" variant="outlined" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      ),
    });
  };

  useEffect(() => {
    if (prevLoadingState && !loadingState) {
      if (!isInitial.current) {
        closeLastSnackbar();

        if (!errorState) {
          lastSnackbarKey.current = showSuccessNotification();
        } else {
          lastSnackbarKey.current = showErrorNotification();
        }
      } else {
        isInitial.current = false;
      }
    }
  }, [loadingState, prevLoadingState, errorState]);

  return {};
};

export default useSettingsNotifications;
